import React, { useEffect, useState } from 'react';
import ReactApexCharts from 'react-apexcharts';
import moment from 'moment';
import { Box, Typography } from '@mui/material';
import axios from 'axios';
import { useSelector } from 'react-redux';

const AreaChart = () => {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartOptions, setChartOptions] = useState(null);
  const currentDate = useSelector((state) => state.customization.currentDate);

  const generateCompleteTimeCategories = (startTime, endTime, intervalMinutes = 60) => {
    const categories = [];
    let current = moment(startTime, 'HH:mm');
    const end = moment(endTime, 'HH:mm');

    while (current.isSameOrBefore(end)) {
      categories.push(current.format('HH:mm'));
      current.add(intervalMinutes, 'minutes');
    }

    return categories;
  };

  const fetchChartData = async () => {
    try {
      setLoading(true);
      const response = await axios.post('https://neoe2e.neophyte.live/fm-api/kpis/line_chart_track_id', {
        startDate: currentDate,
        endDate: currentDate,
        label: ''
      });

      const data = response.data;

      const sortedData = data.sort((a, b) => new Date(a.time) - new Date(b.time));

      const labels = [...new Set(sortedData.flatMap((item) => item.counts.map((count) => count.label)))];

      const firstTime = sortedData[0] ? moment(sortedData[0].time).format('HH:mm') : '00:00';
      const lastTime = sortedData[sortedData.length - 1] ? moment(sortedData[sortedData.length - 1].time).format('HH:mm') : '23:59';

      const completeCategories = generateCompleteTimeCategories(firstTime, lastTime);

      // Create a map for quick lookup of existing data points
      const timeDataMap = {};
      sortedData.forEach((item) => {
        const timeKey = moment(item.time).format('HH:mm');
        timeDataMap[timeKey] = item.counts;
      });
      const seriesData = labels.map((label) => ({
        name: label,
        data: completeCategories.map((time) => {
          if (timeDataMap[time]) {
            const count = timeDataMap[time].find((c) => c.label === label);
            return count ? count.count : 0;
          }
          return 0;
        })
      }));

      setSeries(seriesData);

      const categories = completeCategories;

      const options = {
        chart: {
          animations: {
            enabled: true,
            easing: 'easeInOutQuad',
            speed: 800,
            animateGradually: {
              enabled: true,
              delay: 150
            },
            dynamicAnimation: {
              enabled: true,
              speed: 350
            }
          },
          type: 'area',
          height: 450,
          stacked: false,
          toolbar: { show: false },
          zoom: { enabled: true }
        },
        dataLabels: { enabled: false },
        stroke: {
          curve: 'smooth',
          width: 2,
          lineCap: 'round',
          fill: {
            type: 'solid',
            opacity: 0.3
          }
        },

        fill: {
          type: 'gradient',
          gradient: {
            opacityFrom: 0.4,
            opacityTo: 0.1,
            stops: [0, 100]
          }
        },
        markers: {
          size: 0,
          hover: {
            size: 5
          }
        },
        legend: {
          position: 'top',
          horizontalAlign: 'left',
          fontSize: '12px',
          itemMargin: {
            horizontal: 8,
            vertical: 3
          }
        },
        xaxis: {
          type: 'category',
          categories: categories,
          labels: {
            style: { fontSize: '12px' },
            rotate: -45,
            rotateAlways: true
          },
          title: {
            text: 'TimeStamps',
            style: {
              fontSize: '12px'
            }
          }
        },
        yaxis: {
          title: {
            text: 'Count',
            style: {
              fontSize: '12px'
            }
          },
          labels: {
            formatter: (val) => Math.round(val),
            style: { fontSize: '12px' }
          },
          min: 0
        },
        tooltip: {
          shared: true,
          intersect: false,
          x: {
            format: 'HH:mm'
          }
        },
        grid: {
          show: true,
          borderColor: '#f1f1f1',
          strokeDashArray: 0,
          position: 'back'
        }
      };

      setChartOptions(options);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching chart data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChartData();
  }, [currentDate]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={450}>
        <Typography variant="h6" color="textSecondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!series || series.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={450}>
        <Typography variant="h6" color="textSecondary">
          No data available
        </Typography>
      </Box>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', minHeight: '500px' }}>
      <ReactApexCharts options={chartOptions} series={series} type="area" height={450} width="100%" />
    </div>
  );
};

export default AreaChart;
